<template>
  <v-card>
    <v-card-text>
      <v-card-title>
        Scheduled Campaigns
        <refresh-spinner v-model="isRefreshingData" @click="refreshList"></refresh-spinner>
        <v-spacer></v-spacer>
        <v-text-field
          @keydown.native.escape="searchTerm=''"
          label="Filter campaigns in calendar view"
          single-line
          hide-details
          clearable
          v-model="searchTerm">
        </v-text-field>
        <template v-if="allowCreateNew">
          <v-spacer></v-spacer>
          <v-btn color="primary" :to="{'name': 'new_email_campaign_path'}">
            <v-icon left>mdi-plus</v-icon>
            New Campaign
          </v-btn>
        </template>
      </v-card-title>
      <v-row class="fill-height">
        <v-col>
          <v-sheet height="64">
            <v-toolbar flat color="white">

              <v-btn outlined class="mr-4" color="grey darken-2" @click="setToday">
                Today
              </v-btn>
              <v-btn fab text small color="grey darken-2" @click="prev">
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn fab text small color="grey darken-2" @click="next">
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
              <v-toolbar-title>{{ title }}</v-toolbar-title>

              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="scheduleListViewIndex"
                mandatory
              >
                <v-btn outlined color="grey darken-2"><v-icon>mdi-calendar-clock</v-icon></v-btn>
                <v-btn outlined color="grey darken-2"><v-icon>mdi-view-list</v-icon></v-btn>
              </v-btn-toggle>

              <v-spacer></v-spacer>
              <v-menu bottom right>
                <template v-slot:activator="{ on }">
                  <v-btn
                    outlined
                    color="grey darken-2"
                    v-on="on"
                  >
                    <span>{{ typeToLabel[type] }}</span>
                    <v-icon right>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="type = 'day'">
                    <v-list-item-title>Day</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = 'week'">
                    <v-list-item-title>Week</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = 'month'">
                    <v-list-item-title>Month</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = '4day'">
                    <v-list-item-title>4 days</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </v-sheet>
          <scheduled-email-campaigns-list
            v-show="scheduleView === 'list'"
            :all-dealers="allDealers"
            :show-dealers-column="allDealers"
            :search-term="searchTerm"
            :props-to-search="propsToSearch"
            :scheduled-campaigns-data="scheduledCampaignsData"
            v-on:edit-campaign="handleEditCampaign"
          >
          </scheduled-email-campaigns-list>

          <v-sheet height="600" v-show="scheduleView === 'calendar'">
            <v-calendar
              ref="calendar"
              v-model="focus"
              color="primary"
              event-text-color="black"
              :events="events"
              :event-color="getEventColor"
              :now="today"
              :type="type"
              @click:event="showEvent"
              @click:more="viewDay"
              @click:date="viewDay"
              @change="updateRange"
            ></v-calendar>
            <v-menu
              v-model="selectedOpen"
              :close-on-content-click="false"
              :activator="selectedElement"
              offset-x
            >
              <v-bottom-sheet
                v-model="selectedOpen"
                persistent
              >
                <v-card>
                  <v-tabs v-model="campaignTabIndex" centered icons-and-text background-color="grey lighten-4">
                    <v-spacer/>
                    <v-tab>Stats<v-icon>mdi-file-chart</v-icon></v-tab>
                    <v-tab>Edit<v-icon>mdi-pencil</v-icon></v-tab>
                    <v-spacer/>
                    <v-btn fab x-small dark class="mr-4 mt-4" @click="onEmailCampaignCancel">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-tabs>
                  <v-tabs-items v-model="campaignTabIndex" @change="alert('change')">
                    <v-tab-item>
                      <email-campaign-stats
                        :email-campaign-id="selectedEvent.email_campaign_id"
                      >
                      </email-campaign-stats>
                    </v-tab-item>
                    <v-tab-item>
                      <email-campaign
                        :email-campaign-id="selectedEvent.email_campaign_id"
                        :allow-scheduling="true"
                        :show-cancel="true"
                        v-on:save="onEmailCampaignSave"
                        v-on:cancel="onEmailCampaignCancel"
                      />
                    </v-tab-item>
                  </v-tabs-items>
                </v-card>
              </v-bottom-sheet>
            </v-menu>
          </v-sheet>
        </v-col>
      </v-row>
  </v-card-text>
  </v-card>
</template>

<script>
import * as R from 'ramda'
import { mapState } from 'vuex'
import 'chartjs-plugin-colorschemes/src/plugins/plugin.colorschemes'
import EmailCampaign from './EmailCampaign'
import CampaignStats from './CampaignStats'
import ScheduledCampaignsList from './ScheduledCampaignsList'
import RefreshSpinner from '@/components/ui/RefreshSpinner'
import { SearchFilterMixin } from '@/components/mixins'
import { EmailCampaignApi } from '@/services'
import { CHART_COLORS } from '@/components/charts'
import { DateTime } from 'luxon'

export default {
  name: 'scheduled-email-campaigns',
  mixins: [SearchFilterMixin],
  components: {
    [RefreshSpinner.name]: RefreshSpinner,
    [EmailCampaign.name]: EmailCampaign,
    [ScheduledCampaignsList.name]: ScheduledCampaignsList,
    [CampaignStats.name]: CampaignStats
  },
  props: {
    allDealers: {
      required: true,
      type: Boolean,
      default: false
    },
    allowCreateNew: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    focus: '',
    type: 'month',
    typeToLabel: {
      month: 'Month',
      week: 'Week',
      day: 'Day',
      '4day': '4 Days'
    },
    start: null,
    end: null,
    scheduledCampaignsData: [],
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    colors: CHART_COLORS,
    showCampaignEditDrawer: false,
    scheduleListViewIndex: 0,
    scheduleListViews: ['calendar', 'list'],
    isRefreshingData: false,
    searchTerm: '',
    propsToSearch: ['dealer_name', 'name'],
    campaignTabIndex: 0
  }),
  computed: {
    ...mapState('DealerStore', ['dealer']),
    scheduleView () {
      return this.scheduleListViews[this.scheduleListViewIndex]
    },
    title () {
      const { start, end } = this
      if (!start || !end) {
        return ''
      }

      const startMonth = this.monthFormatter(start)
      const endMonth = this.monthFormatter(end)
      const suffixMonth = startMonth === endMonth ? '' : endMonth

      const startYear = start.year
      const endYear = end.year
      const suffixYear = startYear === endYear ? '' : endYear

      const startDay = start.day + this.nth(start.day)
      const endDay = end.day + this.nth(end.day)

      switch (this.type) {
        case 'month':
          return `${startMonth} ${startYear}`
        case 'week':
        case '4day':
          return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`
        case 'day':
          return `${startMonth} ${startDay} ${startYear}`
      }
      return ''
    },
    monthFormatter () {
      return this.$refs.calendar.getFormatter({
        timeZone: 'UTC', month: 'long'
      })
    },
    today () {
      return this.formatDate(DateTime.now())
    },
    rptTimeframe () {
      return {
        startDate: R.propOr(undefined, ['date'], this.start),
        endDate: R.propOr(undefined, ['date'], this.end)
      }
    },
    // filtered campaigns...
    scheduledCampaigns () {
      return this.filterDataBySearchTerm(this.searchTerm, this.propsToSearch, this.scheduledCampaignsData)
    },
    events () {
      // figure out what colors we are going to use
      // if allDealers, all dealer events same color
      // otherwise, different events == diff color

      const colorMap = this.buildColorMapFromCampaigns(this.allDealers, this.scheduledCampaigns)
      return this.scheduledCampaigns.map(sCamp => {
        const startAt = DateTime.fromISO(sCamp.scheduled_at)
        const endAt = startAt.plus({ minutes: 15 })

        return {
          name: this.allDealers ? `${sCamp.name} (${sCamp.dealer_name})` : sCamp.name,
          start: this.formatDate(startAt),
          end: this.formatDate(endAt),
          color: colorMap[this.allDealers ? sCamp.dealer_id : sCamp.id],
          email_campaign_id: sCamp.id,
          dealer_id: sCamp.dealer_id
        }
      })
    }

  },
  watch: {
    dealer (newVal) {
      this.updateRange({ start: this.start, end: this.end })
    }
  },
  mounted () {
    this.$refs.calendar.checkChange()
  },
  methods: {
    viewDay ({ date }) {
      this.focus = date
      this.type = 'day'
    },
    getEventColor (event) {
      return event.color
    },
    setToday () {
      this.focus = this.today
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    async showEvent ({ nativeEvent, event }) {
      this.selectedElement = nativeEvent.target
      await this.showCampaign(event)
      nativeEvent.stopPropagation()
    },
    async showCampaign (event) {
      if (event.dealer_id !== this.dealer.id) {
        await this.$store.dispatch('DealerStore/getDealer', { id: event.dealer_id })
      }
      this.campaignTabIndex = 0
      this.selectedEvent = event
      this.selectedOpen = true
    },
    async updateRange ({ start, end }) {
      if (!start && !end) {
        return
      }
      this.isRefreshingData = true
      let dealerId = null
      if (!this.allDealers) {
        dealerId = this.dealer.id
      }

      try {
        const resp = await EmailCampaignApi.upcomingByDate(dealerId, start.date, end.date)
        this.scheduledCampaignsData = resp.data
        this.start = start
        this.end = end
        this.isRefreshingData = false
      } catch (error) {
        this.$flash({
          showClose: true,
          message: 'Error refreshing Email Campaign schedules ' + error.message,
          type: 'error',
          duration: 0
        })
        this.isRefreshingData = false
      }
    },
    nth (d) {
      return d > 3 && d < 21
        ? 'th'
        : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10]
    },
    rnd (a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a
    },
    formatDate (a) {
      return a.toFormat('yyyy-MM-dd HH:mm')
    },
    buildColorMapFromCampaigns (isAllDealers, campaigns) {
      let grpFn
      if (isAllDealers) {
        grpFn = (campaign) => { return campaign.dealer_id }
      } else {
        grpFn = (campaign) => { return campaign.id }
      }

      const colorsByName = R.groupBy(grpFn)
      const grouped = colorsByName(campaigns)

      return Object.keys(grouped).reduce((acc, cur, idx) => {
        acc[cur] = this.colors[idx % this.colors.length]
        return acc
      }, {})
    },
    onEmailCampaignSave (emailCampaign) {
      this.$flash({
        showClose: true,
        message: `Saved Email Campaign ${emailCampaign.name}`,
        type: 'mini',
        duration: 2500
      })
      // refresh in case times changed per save...
      this.updateRange({ start: this.start, end: this.end })
      this.selectedOpen = false
    },
    onEmailCampaignCancel () {
      this.selectedOpen = false
    },
    refreshList () {
      this.updateRange({ start: this.start, end: this.end })
    },
    handleEditCampaign (editCampaignEvent) {
      const e = this.events.find(evt => { return evt.email_campaign_id === editCampaignEvent.id })
      if (!e) {
        this.$flash({
          showClose: true,
          message: 'Error handling edit for email campaign.',
          type: 'mini',
          duration: 3500
        })
      } else {
        this.showCampaign(e)
      }
    }
  }
}
</script>
